import { useEffect } from "react"
import { useNavigate, Outlet } from "react-router-dom"
import { Sidebar } from "@/components/ui/sidebar"
import { SidebarNavigation } from "@/components/layout/SidebarNavigation"
import { SidebarFooter } from "@/components/layout/SidebarFooter"
import { SidebarProfile } from "@/components/layout/SidebarProfile"
import { MobileMenu } from "@/components/layout/MobileMenu"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { SidebarProvider } from "@/contexts/SidebarContext"

export function Layout() {
  const navigate = useNavigate()
  const { toast } = useToast()

  // Initial session check
  useEffect(() => {
    const checkSession = async () => {
      try {
        // Primeiro, tenta obter a sessão atual
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error("Erro ao obter sessão:", error)
          await supabase.auth.signOut()
          navigate("/login")
          return
        }

        // Se não há sessão, redireciona para login
        if (!session) {
          console.log("Nenhuma sessão encontrada")
          navigate("/login")
          return
        }

        console.log("Sessão atual:", session)

        // Verifica se a sessão está próxima de expirar (menos de 5 minutos)
        const expiresAt = session.expires_at
        const now = Math.floor(Date.now() / 1000)
        const fiveMinutes = 5 * 60

        if (expiresAt && (expiresAt - now) < fiveMinutes) {
          console.log("Sessão próxima de expirar, tentando renovar...")
          
          try {
            const { data, error: refreshError } = await supabase.auth.refreshSession()
            
            if (refreshError) {
              console.error("Erro ao renovar sessão:", refreshError)
              await supabase.auth.signOut()
              navigate("/login")
              return
            }

            if (!data.session) {
              console.error("Sessão não retornada após renovação")
              await supabase.auth.signOut()
              navigate("/login")
              return
            }

            console.log("Sessão renovada com sucesso:", data.session)
          } catch (refreshError) {
            console.error("Erro ao tentar renovar sessão:", refreshError)
            await supabase.auth.signOut()
            navigate("/login")
            return
          }
        }
      } catch (error) {
        console.error("Erro de autenticação:", error)
        await supabase.auth.signOut()
        navigate("/login")
        toast({
          variant: "destructive",
          title: "Erro de autenticação",
          description: "Por favor, faça login novamente.",
        })
      }
    }

    checkSession()
  }, [navigate, toast])

  // Profile query
  const { data: profile, isError } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error("User not found")
      }

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single()

      if (error) throw error
      return data
    },
    retry: 1,
    meta: {
      onError: () => {
        navigate("/login")
        toast({
          variant: "destructive",
          title: "Erro de autenticação",
          description: "Por favor, faça login novamente.",
        })
      }
    }
  })

  // Auth state change listener
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth state changed:", event, session)
      
      if (event === "SIGNED_OUT" || !session) {
        navigate("/login")
        toast({
          variant: "destructive",
          title: "Sessão encerrada",
          description: "Por favor, faça login novamente.",
        })
      } else if (event === "TOKEN_REFRESHED") {
        console.log("Token renovado com sucesso")
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [navigate, toast])

  if (isError) return null

  return (
    <SidebarProvider>
      <div className="flex h-full min-h-screen w-full bg-gradient-to-br from-background to-background/95">
        {/* Menu móvel */}
        <MobileMenu />
        
        {/* Sidebar desktop */}
        <div className="hidden md:block">
          <Sidebar>
            <div className="flex h-full flex-col">
              <div className="flex-1">
                <SidebarProfile profile={profile} />
                <SidebarNavigation />
              </div>
              <SidebarFooter />
            </div>
          </Sidebar>
        </div>

        <main className="flex-1 overflow-y-auto md:pt-0 pt-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </SidebarProvider>
  )
}